import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { value: "partnerschaft", label: "Interesse an Partnerschaft" },
  { value: "investition", label: "Interesse an Investition" },
  { value: "beides", label: "Interesse an beidem" },
];

const InvestorForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    volume: "",
    message: "",
    request_pitch_deck: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.volume) {
      toast.error("Bitte Name, E-Mail und Anliegen angeben.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/investor-inquiries`, form);
      setSubmitted(true);
      toast.success("Pitch Booklet wurde an Ihre E-Mail-Adresse versendet.");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Übermittlung fehlgeschlagen. Bitte erneut versuchen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="investor-form-section"
      className="relative py-24 md:py-36 bg-[#080808] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <span data-testid="form-eyebrow" className="label-eyebrow">
              Partner werden
            </span>
            <h2 className="font-heading mt-6 text-4xl sm:text-5xl font-light leading-[1.05] tracking-tighter">
              Werden Sie Teil
              <br />
              <span className="text-[#c88a53] italic font-extralight">
                der Vision.
              </span>
            </h2>
            <p className="mt-8 text-base md:text-lg text-white/65 leading-relaxed font-light">
              Hinterlassen Sie uns Ihre Eckdaten — und Sie erhalten das Pitch
              Booklet <span className="text-white">direkt nach Absenden
              automatisch per E-Mail</span>. Ein vertrauliches Erstgespräch
              folgt typischerweise innerhalb von 48 Stunden.
            </p>

            <div className="mt-10 text-sm text-white/50 leading-relaxed">
              <div>
                <span className="text-white/80">E-Mail · </span>
                <a
                  href="mailto:info@monowood.ch"
                  data-testid="contact-email"
                  className="text-[#c88a53] hover:text-[#d99c66]"
                >
                  info@monowood.ch
                </a>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {submitted ? (
              <div
                data-testid="form-success"
                className="border border-[#c88a53]/40 bg-[#c88a53]/5 p-10 md:p-14 mw-rise"
              >
                <div className="w-14 h-14 border border-[#c88a53] flex items-center justify-center">
                  <Check className="text-[#c88a53]" size={24} strokeWidth={1.5} />
                </div>
                <h3 className="mt-8 font-heading text-3xl text-white font-light tracking-tight">
                  Danke, {form.name.split(" ")[0]}.
                </h3>
                <p className="mt-4 text-white/70 max-w-xl leading-relaxed">
                  Das Pitch Booklet ist auf dem Weg in Ihr Postfach
                  (<span className="text-white">{form.email}</span>). Ein
                  vertrauliches Erstgespräch folgt innerhalb von 48 Stunden.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="investor-form"
                className="border border-white/10 bg-[#0a0a0a] p-8 md:p-10 space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Name" required>
                    <input
                      type="text"
                      data-testid="form-name"
                      className="mw-input"
                      placeholder="Max Mustermann"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label="E-Mail" required>
                    <input
                      type="email"
                      data-testid="form-email"
                      className="mw-input"
                      placeholder="m.mustermann@firma.com"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      required
                    />
                  </Field>
                </div>

                <Field label="Firma / Organisation (optional)">
                  <input
                    type="text"
                    data-testid="form-company"
                    className="mw-input"
                    placeholder="Mustermann AG"
                    value={form.company}
                    onChange={(e) => setField("company", e.target.value)}
                  />
                </Field>

                <Field label="Ihr Anliegen" required>
                  <Select
                    value={form.volume}
                    onValueChange={(v) => setField("volume", v)}
                  >
                    <SelectTrigger
                      data-testid="form-volume-trigger"
                      className="mw-input flex items-center"
                    >
                      <SelectValue placeholder="Anliegen wählen …" />
                    </SelectTrigger>
                    <SelectContent
                      data-testid="form-volume-content"
                      className="bg-[#0a0a0a] border border-white/10 text-white rounded-none"
                    >
                      {INTERESTS.map((v) => (
                        <SelectItem
                          key={v.value}
                          value={v.value}
                          data-testid={`form-volume-option-${v.value}`}
                          className="rounded-none focus:bg-[#c88a53]/15 focus:text-[#c88a53]"
                        >
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Nachricht (optional)">
                  <textarea
                    data-testid="form-message"
                    className="mw-textarea"
                    placeholder="Hintergrund, Fokus, Zeitrahmen …"
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                  />
                </Field>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.request_pitch_deck}
                    onCheckedChange={(v) =>
                      setField("request_pitch_deck", Boolean(v))
                    }
                    data-testid="form-deck-checkbox"
                    className="mt-0.5 rounded-none border-white/20 data-[state=checked]:bg-[#c88a53] data-[state=checked]:border-[#c88a53] data-[state=checked]:text-black"
                  />
                  <span className="text-sm text-white/65 leading-relaxed">
                    Pitch Booklet (PDF, vertraulich) direkt nach Absenden per
                    E-Mail zusenden.
                  </span>
                </label>

                <div className="pt-2 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <p className="text-xs text-white/40 max-w-md leading-relaxed">
                    Mit Absenden bestätigen Sie, dass Ihre Angaben vertraulich
                    behandelt werden. Keine Weitergabe an Dritte.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit"
                    className="btn-primary text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Senden …
                      </>
                    ) : (
                      <>
                        Anfrage senden
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-white/55 font-semibold mb-2">
      {label}
      {required && <span className="text-[#c88a53] ml-1">*</span>}
    </span>
    {children}
  </label>
);

export default InvestorForm;
